import React, { useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { pageSamples } from '../data/content';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
export function InsidePages() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState<number>(0);
  const total = pageSamples.length;
  const goTo = (next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(total - 1, next));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setIndex(clamped);
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current !== index) setIndex(current);
  };
  return <section id="inside-pages" className="w-full bg-bone px-5 py-20 sm:py-28" data-id="element-139" data-mp="3kuezo35hyisa">
      <div className="mx-auto max-w-6xl" data-id="element-140" data-mp="1mlug404bjm0z">
        <SectionHeading eyebrow="Páginas reales" title={<>
              Mirá el material <span className="text-brand" data-id="element-142" data-mp="1rke3m4yk95vg">por dentro</span>
            </>} subtitle="Fichas, infografías y circuitos tal como los vas a imprimir, desde 1º Primaria hasta 6º Secundaria." data-id="element-141" data-mp="1mmw2v1up5wyj" />

        <Reveal delay={0.06} data-id="element-143" data-mp="1huui0rjgqxww">
          <div ref={trackRef} onScroll={handleScroll} role="group" aria-roledescription="carrusel" aria-label="Páginas de ejemplo del material" tabIndex={0} onKeyDown={event => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(index + 1);
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(index - 1);
          }
        }} className="no-scrollbar mt-14 flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-3xl focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/30" data-id="element-144" data-mp="1hu302jisgyp3">
            {pageSamples.map((page, pageIndex) => <article key={page.src} aria-roledescription="slide" aria-label={`${pageIndex + 1} de ${total}`} className="w-full min-w-0 shrink-0 snap-start" data-id="element-145" data-mp="62v7yiqt338j" data-mpi={pageIndex}>
                <div className="grid gap-8 md:grid-cols-[0.95fr_1.05fr] md:items-center" data-id="element-146" data-mp="15d95l6i64e9">
                  <div className="relative min-w-0 overflow-hidden rounded-3xl border-2 border-ink bg-white p-3 shadow-lift sm:p-5" data-id="element-147" data-mp="3t7bg9yryayxp">
                    <img src={page.src} alt={`${page.title} — página de ejemplo, ${page.level}`} loading={pageIndex === 0 ? 'eager' : 'lazy'} className="block h-auto w-full rounded-xl" data-id="element-148" data-mp="osto7m4rc2n9" />
                    <button type="button" onClick={() => goTo(pageIndex - 1)} aria-label="Página anterior" className="absolute left-4 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-ink bg-white text-ink transition-[transform,background-color,color] duration-150 ease-out hover:scale-105 hover:bg-brand hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/30 sm:left-7" data-id="element-149" data-mp="ktm23xfveeet">
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-150" data-mp="1b2jxuve5wl7m" />
                    </button>
                    <button type="button" onClick={() => goTo(pageIndex + 1)} aria-label="Página siguiente" className="absolute right-4 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border-2 border-ink bg-white text-ink transition-[transform,background-color,color] duration-150 ease-out hover:scale-105 hover:bg-brand hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/30 sm:right-7" data-id="element-151" data-mp="1g0v8378dcxqd">
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-152" data-mp="23po04slvelfp" />
                    </button>
                  </div>

                  <div className="min-w-0 md:pl-2" data-id="element-153" data-mp="28ohaic5v1wbn">
                    <p className="inline-flex rounded-full bg-ink px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-volt" data-id="element-154" data-mp="2cr49cx0y8hic">
                      {page.level}
                    </p>
                    <h3 className="mt-5 font-display text-3xl leading-tight text-ink sm:text-4xl" data-id="element-155" data-mp="242gl436ven6y">
                      {page.title}
                    </h3>
                    <p className="mt-4 text-base leading-relaxed text-ink/70 sm:text-lg" data-id="element-156" data-mp="1zzaciowacjxb">
                      {page.caption}
                    </p>
                    <p className="mt-7 font-display text-sm tabular-nums text-ink/40" data-id="element-157" data-mp="28uq4f0dhcp09">
                      Página {pageIndex + 1} de {total}
                    </p>
                  </div>
                </div>
              </article>)}
          </div>
        </Reveal>

        <div className="mt-8 flex justify-center gap-3" data-id="element-158" data-mp="1v2a5obrm3d55">
          {pageSamples.map((page, thumbIndex) => <button key={`thumb-${page.src}`} type="button" onClick={() => goTo(thumbIndex)} aria-label={`Ir a la página ${thumbIndex + 1}: ${page.title}`} aria-current={thumbIndex === index} className={`h-20 w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-white transition-[border-color,transform,opacity] duration-150 ease-out hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand/30 ${thumbIndex === index ? 'border-brand' : 'border-ink/15 opacity-55'}`} data-id="element-159" data-mp="1q372yxz3tj0c" data-mpi={thumbIndex}>
              <img src={page.src} alt="" loading="lazy" className="h-full w-full object-cover object-top" data-id="element-160" data-mp="e9wpiqgwyile" />
            </button>)}
        </div>
      </div>
    </section>;
}